import { FileSpreadsheet, Info } from "lucide-react";
import { lazy, Suspense, useState } from "react";
import { useTranslation } from "react-i18next";

import { AccordionItem } from "@krea/shared/design-system/AccordionItem";
import { Button } from "@krea/shared/design-system/Button";
import { LabeledSlider } from "@krea/shared/design-system/LabeledSlider";
import { ResponsiveTooltip } from "@krea/shared/design-system/ResponsiveTooltip";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@krea/shared/design-system/Tabs";
import { Text } from "@krea/shared/design-system/Text";

import { buildRepaymentPlanExportSpec } from "./buildRepaymentPlanExportSpec";
import type { IRepaymentChartRow } from "./RepaymentPlanChart";
import type { IRepaymentPlanBidInfo, IRepaymentPlanData } from "./types";
import { useRepaymentPlanDisplay } from "./useRepaymentPlanDisplay";

const RepaymentPlanChart = lazy(() =>
  import("./RepaymentPlanChart").then((m) => ({
    default: m.RepaymentPlanChart,
  })),
);

interface IRepaymentPlanSectionProps {
  bid: IRepaymentPlanBidInfo;
  // The caller decides whether to render this section at all (admin-gated, and
  // only when a plan exists), so by the time we render a plan is always present.
  plan: IRepaymentPlanData;
  currency: string;
  formatCurrency: (value: string | number) => string;
  // Whole-currency formatter (no decimals) for the chart's axis ticks.
  formatAxisCurrency: (value: string | number) => string;
  formatPaybackTime: (months: number) => string;
  companyName: string | null;
  // Used to name the exported file: amortizationplan_<loanApp>_offer_<lender>_<bid>.
  loanApplicationId: string | null;
  lenderName: string | null;
}

const StatBox = ({
  label,
  value,
  tooltip,
}: {
  label: string;
  value: string;
  // Optional plain-language explanation; when present an info icon is shown
  // that reveals it (hover tooltip on desktop, bottom drawer on mobile).
  tooltip?: string;
}) => (
  <div className="flex min-w-[7rem] flex-1 flex-col gap-1 rounded-lg border border-gray-200 bg-white p-3">
    <div className="flex items-center gap-1">
      <Text className="text-label">{label}</Text>
      {tooltip && (
        <ResponsiveTooltip
          className="max-w-[16rem]"
          trigger={
            <button
              type="button"
              aria-label={label}
              className="inline-flex cursor-help items-center rounded-full text-gray-500 hover:text-gray-700 focus-visible:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/40 focus-visible:ring-offset-1"
            >
              <Info className="size-3.5" />
            </button>
          }
        >
          <p className="text-helper">{tooltip}</p>
        </ResponsiveTooltip>
      )}
    </div>
    <Text className="text-base-strong tabular-nums">{value}</Text>
  </div>
);

export const RepaymentPlanSection = ({
  bid,
  plan,
  currency,
  formatCurrency,
  formatAxisCurrency,
  formatPaybackTime,
  companyName,
  loanApplicationId,
  lenderName,
}: IRepaymentPlanSectionProps) => {
  const { t } = useTranslation("repaymentPlan");
  const [selectedMonths, setSelectedMonths] = useState<number | null>(null);
  const [isExporting, setIsExporting] = useState(false);
  const [planView, setPlanView] = useState("table");

  const fullMonths = plan.entries.length - 1;
  const months =
    selectedMonths === null
      ? fullMonths
      : Math.min(Math.max(selectedMonths, 1), fullMonths);

  const display = useRepaymentPlanDisplay(plan, months);

  const earlyRepaymentFeeLabel =
    bid.isRepayableWithoutFee || !bid.earlyRepaymentFee
      ? t("bids.common.repayableWithoutFee")
      : /^\d+(\.\d+)?$/.test(bid.earlyRepaymentFee.trim())
        ? formatCurrency(Number(bid.earlyRepaymentFee))
        : bid.earlyRepaymentFee;

  const handleExport = async () => {
    setIsExporting(true);
    try {
      const { exportToXlsx } = await import("@krea/shared/utils/exportToXlsx");
      await exportToXlsx(
        buildRepaymentPlanExportSpec({
          bid,
          companyName,
          loanApplicationId,
          lenderName,
          earlyRepaymentFeeLabel,
          formatCurrency,
          formatPaybackTime,
          t,
          displayPlan: display.displayPlan,
          moneyColumns: display.moneyColumns,
          cellValue: display.cellValue,
          totalValue: display.totalValue,
          hasFees: display.hasFees,
        }),
      );
    } catch (error) {
      console.error("Failed to export amortisation plan", error);
    } finally {
      setIsExporting(false);
    }
  };

  const renderBody = () => {
    const { displayPlan, hasFees, moneyColumns, cellValue, totalValue } =
      display;
    const monthsList = Array.from({ length: fullMonths }, (_, i) => i + 1);

    const chartRows: IRepaymentChartRow[] = displayPlan.entries.map(
      (entry) => ({
        month: entry.month,
        principal: Number(entry.principal),
        interest: Number(entry.interest),
        fees: cellValue(entry, "fees"),
        balance: Number(entry.remainingBalance),
      }),
    );

    return (
      <div data-testid="repayment-plan-section">
        <Tabs value={planView} onValueChange={setPlanView}>
          <div className="mb-3 flex items-center justify-between gap-3">
            <TabsList>
              <TabsTrigger value="table">{t("tabs.table")}</TabsTrigger>
              <TabsTrigger value="graph">{t("tabs.graph")}</TabsTrigger>
            </TabsList>
            {planView === "table" && (
              <Button
                variant="secondary"
                size="sm"
                onClick={handleExport}
                disabled={isExporting}
                className="shrink-0"
              >
                <FileSpreadsheet className="h-4 w-4" />
                {t(isExporting ? "exporting" : "exportToExcel")}
              </Button>
            )}
          </div>

          {fullMonths > 1 && (
            <div className="mb-4" data-testid="repayment-months-slider">
              <div className="mb-1 flex items-baseline gap-2">
                <Text className="text-label">{t("slider.label")}:</Text>
                <Text className="text-base-strong">
                  {months} {t("commons.month", { count: months })}
                </Text>
                <Text className="text-helper">
                  ({t("slider.originalTerm")}: {fullMonths}{" "}
                  {t("commons.month", { count: fullMonths })})
                </Text>
              </div>
              <LabeledSlider
                value={months}
                valuesList={monthsList}
                onChange={setSelectedMonths}
                showMinMax={false}
              />
            </div>
          )}

          <TabsContent value="table">
            {/* h-96 matches the chart height so switching tabs doesn't shift
                the layout. */}
            <div className="h-96 overflow-auto rounded-lg border border-gray-200">
              <table className="w-full border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="bg-gray-100">
                    <th className="text-table-header sticky top-0 z-10 bg-gray-100 py-3 pl-3 pr-2 text-left">
                      {t("month")}
                    </th>
                    {moneyColumns.map((key) => (
                      <th
                        key={key}
                        className="text-table-header sticky top-0 z-10 min-w-[120px] bg-gray-100 py-3 pr-2 text-right"
                      >
                        {t(key)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {displayPlan.entries.map((entry) => (
                    <tr
                      key={entry.month}
                      data-testid="repayment-row"
                      className="bg-white transition-colors hover:bg-primary-150"
                    >
                      <td className="border-t py-2 pl-3 pr-2 text-table-cell">
                        {entry.month}
                      </td>
                      {moneyColumns.map((key) => (
                        <td key={key} className="border-l border-t p-1">
                          <span className="block w-full px-1 py-1 text-right text-table-cell tabular-nums">
                            {formatCurrency(cellValue(entry, key))}
                          </span>
                        </td>
                      ))}
                    </tr>
                  ))}

                  <tr className="bg-gray-100 transition-colors hover:bg-gray-200">
                    <td className="border-t py-2 pl-3 pr-2 text-table-cell font-semibold">
                      {t("total")}
                    </td>
                    {moneyColumns.map((key) => {
                      const total = totalValue(key);
                      return (
                        <td key={key} className="border-l border-t p-1">
                          <span className="block w-full px-1 py-1 text-right text-table-cell font-semibold tabular-nums">
                            {total === null ? "" : formatCurrency(total)}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                </tbody>
              </table>
            </div>
          </TabsContent>

          <TabsContent value="graph">
            <Suspense
              fallback={
                <Text className="text-helper block py-2">{t("loading")}</Text>
              }
            >
              <RepaymentPlanChart
                rows={chartRows}
                hasFees={hasFees}
                formatCurrency={formatCurrency}
                formatAxisCurrency={formatAxisCurrency}
                currency={currency}
              />
            </Suspense>
          </TabsContent>
        </Tabs>

        <div className="mt-4">
          <Text className="text-base-strong mb-2 block">
            {t("totalsTitle")}
          </Text>
          <div className="flex flex-wrap gap-3">
            <StatBox
              label={t("setupFee")}
              value={formatCurrency(Number(displayPlan.totals.setupFee))}
              tooltip={t("tooltips.setupFee")}
            />
            <StatBox
              label={t("totalCostOfCredit")}
              value={formatCurrency(
                Number(displayPlan.totals.totalCostOfCredit),
              )}
              tooltip={t("tooltips.totalCostOfCredit")}
            />
            <StatBox
              label={t("totalRepaid")}
              value={formatCurrency(Number(displayPlan.totals.totalRepaid))}
              tooltip={t("tooltips.totalRepaid")}
            />
          </div>
        </div>

        {displayPlan.isEarly && (
          <div
            className="-mx-4 -mb-3 mt-4 rounded-b-xl bg-mint-100 px-4 py-4 md:-mx-6 md:-mb-7 md:px-6"
            data-testid="repayment-savings"
          >
            <Text className="text-base-strong mb-1 block">
              {t("savings.title")}
            </Text>
            <Text className="text-helper mb-3 block">
              {t("savings.repaidEarlier", {
                duration: formatPaybackTime(displayPlan.monthsSaved),
              })}
            </Text>
            <div className="flex flex-wrap gap-3">
              <StatBox
                label={t("savings.interestSaved")}
                value={formatCurrency(displayPlan.interestSaved)}
                tooltip={t("tooltips.interestSaved")}
              />
              {hasFees && (
                <StatBox
                  label={t("savings.feesSaved")}
                  value={formatCurrency(displayPlan.feesSaved)}
                  tooltip={t("tooltips.feesSaved")}
                />
              )}
              <StatBox
                label={t("savings.grossSaved")}
                value={formatCurrency(displayPlan.grossSaved)}
                tooltip={t("tooltips.grossSaved")}
              />
              <StatBox
                label={t("savings.earlyRepaymentFee")}
                value={earlyRepaymentFeeLabel}
                tooltip={t("tooltips.earlyRepaymentFee")}
              />
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <AccordionItem
      title={
        <span className="flex flex-col gap-1">
          <Text htmlTag="span" className="text-subsection">
            {t("title")}
          </Text>
          <Text htmlTag="span" className="text-helper font-normal">
            {t("disclaimer")}
          </Text>
        </span>
      }
      titleStyling="mb-0"
      className="mt-4 rounded-xl bg-white"
      separatorWhenOpen
    >
      {renderBody()}
    </AccordionItem>
  );
};
